import { Box, Button, Paper, Stack, Typography, useTheme } from "@mui/material";
import React from "react";
import { IFileUpload, IFileUploadV2Props } from "./FileUpload.type";
import {
  CheckOutlined,
  DescriptionOutlined,
  KeyboardArrowDownRounded,
  UploadFileOutlined,
  CloseOutlined,
  ImageOutlined,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { MultiFileDisplay } from "./FileUpload";
import { getUniqueId } from "../../helpers";

export const FileUploadV2: React.FC<IFileUploadV2Props> = (props) => {
  const {
    height,
    value,
    onChange,
    onDelete,
    deleteDisabled = false,
    actionButton,
    multiple,
    onMultiChange,
    displayText = "Drop documents here to get signed"
  } = props;
  const theme = useTheme();
  const [file, setFile] = React.useState<File | null>(props.file || null);

  // Extract filename from URL or use file name
  const getFileName = () => {
    if (file) {
      return file.name;
    }
    if (typeof value === "string" && value) {
      // Extract filename from URL
      const urlParts = value.split("/");
      return urlParts[urlParts.length - 1] || "Selected file";
    }
    return null;
  };

  const fileName = getFileName();
  const hasFile = !!(file || (typeof value === "string" && value));

  const onDrop =
    (acceptedFiles: File[]) => {
      const files = acceptedFiles;
      if (files && files.length > 0 && !multiple) {
        const file = files[0];
        if (props.onChange) {
          setFile(file);
          props.onChange(file);
        }
      } else if (
        files &&
        files.length > 0 &&
        Array.isArray(value) &&
        multiple
      ) {
        const file = files[0];
        if (onMultiChange) {
          onMultiChange([
            ...value,
            {
              key: getUniqueId(),
              file: file,
              name: file.name,
              path: null,
            },
          ]);
        }
      }
    };

  const handleMultiChange = (data: IFileUpload[]) => {
    if (onMultiChange) {
      onMultiChange(data);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleDelete = () => {
    setFile(null);
    if (onDelete) {
      onDelete();
    }
    if (onChange) {
      // Call onChange with null to clear the selection
      onChange(null as any);
    }
  };

  if ((value || file) && multiple && !Array.isArray(value)) {
    return (
      <Paper>
        <Stack
          spacing={2}
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          padding={1}
          sx={{
            minHeight: height || "300px",
            border: `2px dashed ${theme.palette.grey[500]}`,
          }}
        >
          <Stack
            direction={"column"}
            spacing={1}
            alignItems={"center"}
            justifyContent={"center"}
            width={"80%"}
          >
            <Box position={"relative"}>
              <DescriptionOutlined
                sx={{ fontSize: 75, color: theme.palette.grey[500] }}
              />
              <Box
                sx={{
                  position: "absolute",
                  padding: "2px",
                  right: 3,
                  background: theme.palette.success.main,
                  borderRadius: "100%",
                  display: "flex",
                  top: -1,
                }}
              >
                <CheckOutlined sx={{ color: "#fff" }} />
              </Box>
            </Box>

            <Typography
              variant="body1"
              fontSize={"1rem"}
              fontWeight={600}
              sx={{ color: theme.palette.grey[600] }}
            >
              {value?.toString() || file?.name}
            </Typography>
          </Stack>
          <Stack direction={"row"} spacing={1}>
            <Button
              variant="contained"
              size="large"
              color="error"
              onClick={handleDelete}
            >
              Remove
            </Button>
            {actionButton && (
              <Button
                variant="contained"
                size="large"
                color="primary"
                onClick={actionButton.onClick}
              >
                {actionButton.text}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  // Show file selected state for single file upload
  if (hasFile && !multiple) {
    return (
      <Paper
        sx={{
          padding: 2,
          border: `2px solid ${theme.palette.success.main}`,
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center" flex={1}>
            <Box
              sx={{
                padding: 1.5,
                borderRadius: 1,
                background: theme.palette.success.light,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {typeof value === "string" && value.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <ImageOutlined sx={{ color: theme.palette.success.main }} />
              ) : (
                <DescriptionOutlined sx={{ color: theme.palette.success.main }} />
              )}
            </Box>
            <Box flex={1}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: theme.palette.grey[800] }}
                noWrap
              >
                {fileName}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.grey[600] }}
              >
                File selected
              </Typography>
            </Box>
          </Stack>
          {!deleteDisabled && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleDelete}
              sx={{ minWidth: "auto", padding: "4px 8px" }}
            >
              <CloseOutlined fontSize="small" />
            </Button>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Stack
        {...getRootProps()}
        spacing={2}
        direction={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          minHeight: height || "300px",
          border: isDragActive
            ? `2.5px dashed ${theme.palette.primary.main}`
            : `2.5px dashed ${theme.palette.grey[400]}`,
          borderRadius: 3,
          boxShadow: isDragActive
            ? `0 0 12px 2px ${theme.palette.primary.light}`
            : `0 2px 8px 0 ${theme.palette.grey[200]}`,
          transition: 'border-color 0.3s, box-shadow 0.3s',
          background: isDragActive ? theme.palette.action.hover : '#fafbfc',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        <>
          <input {...getInputProps()} />
          <UploadFileOutlined
            sx={{
              fontSize: 75,
              color: isDragActive ? theme.palette.primary.main : theme.palette.grey[500],
              transition: 'color 0.3s',
            }}
          />
          <Typography
            variant="body1"
            fontSize={"1rem"}
            fontWeight={600}
            sx={{ color: theme.palette.grey[700], mb: 0.5 }}
          >
            {displayText}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.grey[500], mb: 1 }}
          >
            Accepted file type: <b>.csv</b>
          </Typography>

          <label htmlFor="file-upload">
            <Button
              variant="contained"
              sx={{
                background: isDragActive
                  ? theme.palette.primary.main
                  : theme.palette.grey[200],
                color: isDragActive ? "#fff" : "#222",
                fontSize: "1.2rem",
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: isDragActive ? `0 2px 8px 0 ${theme.palette.primary.light}` : 'none',
                ":hover": {
                  background: theme.palette.primary.main,
                  color: "#fff",
                },
                px: 4,
                py: 1.2,
                mt: 1,
              }}
              size="large"
            >
              Upload <KeyboardArrowDownRounded fontSize="medium" />
            </Button>
          </label>
        </>
      </Stack>
      {multiple && Array.isArray(value) && (
        <MultiFileDisplay value={value} deleteDisabled={deleteDisabled} onChange={handleMultiChange} />
      )}
    </>
  );
};
